/**
 * Router module for Audelio application
 * Handles navigation between different app modes (home, library, exercise, admin)
 */

import appState from './app-state.js';
import logger from './logger.js';
import { i18nService } from '../services/i18n-service.js';
import { can } from '../utils/rbac-helpers.js';
import { Permission } from '../types/rbac.js';
import type { ViewMode } from '../types/app-state.js';

/**
 * Icon types supported by createSVGIcon
 */
type IconType = 'chevron-left' | 'x-mark' | 'chat-bubble';

/**
 * View callbacks for router
 */
interface ViewCallbacks {
  removeTranscriptEventListeners?: (() => void) | null;
  renderHomeView?: (() => void) | null;
  renderLibraryView?: (() => void) | null;
  renderAdminView?: (() => void) | null;
}

/**
 * Create an SVG icon element
 */
function createSVGIcon(type: IconType, className: string = 'icon'): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('stroke-width', type === 'chevron-left' ? '2' : '1.5');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('class', className);
  svg.setAttribute('aria-hidden', 'true');
  
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  
  switch (type) {
    case 'chevron-left':
      path.setAttribute('d', 'M15.75 19.5L8.25 12l7.5-7.5');
      break;
    case 'x-mark':
      path.setAttribute('d', 'M6 18L18 6M6 6l12 12');
      break;
    case 'chat-bubble':
      path.setAttribute('d', 'M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z');
      break;
    default:
      logger.warn('Unknown icon type', { type });
  }
  
  svg.appendChild(path);
  return svg;
}

/**
 * Router class to manage view callbacks
 */
class Router {
  private viewCallbacks: ViewCallbacks;
  
  constructor() {
    this.viewCallbacks = {
      removeTranscriptEventListeners: null,
      renderHomeView: null,
      renderLibraryView: null
    };
  }
  
  /**
   * Register view callbacks
   */
  registerCallbacks(callbacks: Partial<ViewCallbacks>): void {
    this.viewCallbacks = { ...this.viewCallbacks, ...callbacks };
  }
  
  /**
   * Update header actions based on current mode
   * Manages back button in library mode and library button in exercise mode
   */
  updateHeaderActions(): void {
    const headerActions = document.getElementById('header-actions');
    const appIdentity = document.getElementById('app-identity');
    if (!headerActions) return;
    
    // Close mobile menu if open (mode switch should reset menu)
    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    if (headerActions.classList.contains('mobile-menu-open')) {
      headerActions.classList.remove('mobile-menu-open');
      if (mobileMenuBtn) {
        mobileMenuBtn.setAttribute('aria-expanded', 'false');
      }
    }
    
    // Preserve the account/auth button and settings button
    const accountBtn = headerActions.querySelector('#account-btn');
    const settingsBtn = headerActions.querySelector('#settings-btn');
    
    // Clear existing actions
    headerActions.innerHTML = '';
    
    // Re-add account button first (if it exists)
    if (accountBtn) {
      headerActions.appendChild(accountBtn);
    }
    
    // Re-add settings button
    if (settingsBtn) {
      headerActions.appendChild(settingsBtn);
    }
    
    // Add back button in library mode (on the left side)
    if (appState.mode === 'library' && appIdentity) {
      // Check if back button already exists
      let backBtn = appIdentity.querySelector('#header-back-btn');
      if (!backBtn) {
        // Create back button
        backBtn = document.createElement('button');
        backBtn.id = 'header-back-btn';
        backBtn.className = 'btn-reset-focus header-back-btn';
        // Style moved to CSS class 'header-back-btn' for design system consistency
        backBtn.setAttribute('aria-label', i18nService.getString('backToHome') || 'Back to Home');
        
        // Add SVG icon using helper function
        const icon = createSVGIcon('chevron-left', 'icon');
        backBtn.appendChild(icon);
        
        backBtn.addEventListener('click', () => {
          this.switchMode('home');
        });
        
        // Insert back button at the beginning of app-identity
        appIdentity.insertBefore(backBtn, appIdentity.firstChild);
      }
    } else if (appIdentity) {
      // Remove back button if it exists when not in library mode
      const existingBackBtn = appIdentity.querySelector('#header-back-btn');
      if (existingBackBtn) {
        existingBackBtn.remove();
      }
    }
    
    // Only add library button in exercise mode
    if (appState.mode === 'exercise') {
      // Add Library button - matching Account/Settings style
      const libraryBtn = document.createElement('button');
      libraryBtn.id = 'headerLibraryBtn';
      libraryBtn.className = 'btn-reset-focus account-btn';
      libraryBtn.style.position = 'relative';
      libraryBtn.setAttribute('aria-label', i18nService.getString('library') || 'Library');
      
      libraryBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="account-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
        </svg>
        <span class="account-label">${i18nService.getString('library') || 'Library'}</span>
      `;
      
      libraryBtn.addEventListener('click', () => {
        this.switchMode('library');
      });
      
      headerActions.appendChild(libraryBtn);
    }
    
    // Add Clip Management button for users with admin permissions (in all modes except admin)
    if (appState.mode !== 'admin' && can(Permission.VIEW_JOBS)) {
      const adminBtn = document.createElement('button');
      adminBtn.id = 'headerAdminBtn';
      adminBtn.className = 'btn-reset-focus account-btn';
      adminBtn.style.position = 'relative';
      adminBtn.setAttribute('aria-label', 'Clip Management');
      
      adminBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="account-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
        <span class="account-label">Clip Management</span>
      `;
      
      adminBtn.addEventListener('click', () => {
        this.switchMode('admin');
      });
      
      headerActions.appendChild(adminBtn);
    }
  }
  
  /**
   * Switch application mode
   */
  switchMode(mode: ViewMode): void {
    if (mode !== 'home' && mode !== 'library' && mode !== 'exercise' && mode !== 'admin') {
      logger.warn('Invalid app mode', { mode });
      return;
    }
    
    appState.mode = mode;
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.toggle('admin-mode', mode === 'admin');
    }
    
    // Get container elements
    const homeContainer = document.getElementById('home-container');
    const libraryContainer = document.getElementById('library-container');
    const exerciseContainer = document.getElementById('exercise-container');
    const adminContainer = document.getElementById('admin-container');
    
    // Update header actions based on new mode
    this.updateHeaderActions();
    
    // Update visibility based on mode
    if (appState.mode === 'home') {
      // Clean up transcript elements
      if (this.viewCallbacks.removeTranscriptEventListeners) {
        this.viewCallbacks.removeTranscriptEventListeners();
      }
      const transcriptContainers = document.querySelectorAll('.transcript-container');
      transcriptContainers.forEach(container => container.remove());
      appState.transcriptInjected = false;
      document.body.classList.remove('transcript-open');
      appState.transcriptPanelOpen = false;
      
      // Show home, hide others
      if (homeContainer) homeContainer.classList.remove('hidden');
      if (libraryContainer) libraryContainer.classList.add('hidden');
      if (exerciseContainer) exerciseContainer.classList.add('hidden');
      if (adminContainer) adminContainer.classList.add('hidden');
      
      // Render home view
      if (this.viewCallbacks.renderHomeView) {
        this.viewCallbacks.renderHomeView();
      }
      
      // Note: Removed auto-focus on home heading to avoid unwanted selection on page load
    } else if (appState.mode === 'library') {
      // Make sure to clean up any transcript elements
      if (this.viewCallbacks.removeTranscriptEventListeners) {
        this.viewCallbacks.removeTranscriptEventListeners();
      }
      
      // Remove any transcript containers that might be in the DOM
      const transcriptContainers = document.querySelectorAll('.transcript-container');
      transcriptContainers.forEach(container => container.remove());
      
      // Reset the transcript injected flag
      appState.transcriptInjected = false;
      
      // Reset the document body classes
      document.body.classList.remove('transcript-open');
      
      // Reset transcript panel state
      appState.transcriptPanelOpen = false;
      
      // Update UI visibility
      if (homeContainer) homeContainer.classList.add('hidden');
      if (libraryContainer) libraryContainer.classList.remove('hidden');
      if (exerciseContainer) exerciseContainer.classList.add('hidden');
      if (adminContainer) adminContainer.classList.add('hidden');
      
      // Note: Removed auto-focus on library heading to avoid unwanted selection on navigation
      
      // Render the library view with updated clip statuses
      if (this.viewCallbacks.renderLibraryView) {
        this.viewCallbacks.renderLibraryView();
      }
    } else if (appState.mode === 'admin') {
      // Clean up transcript elements
      if (this.viewCallbacks.removeTranscriptEventListeners) {
        this.viewCallbacks.removeTranscriptEventListeners();
      }
      
      // Hide all other containers
      if (homeContainer) homeContainer.classList.add('hidden');
      if (libraryContainer) libraryContainer.classList.add('hidden');
      if (exerciseContainer) exerciseContainer.classList.add('hidden');
      if (adminContainer) adminContainer.classList.remove('hidden');
      
      // Update URL to /admin if not already there
      if (!window.location.pathname.startsWith('/admin')) {
        window.history.pushState({ mode: 'admin' }, '', '/admin');
      }
      
      // Render admin view
      if (this.viewCallbacks.renderAdminView) {
        this.viewCallbacks.renderAdminView();
      }
    } else {
      if (homeContainer) homeContainer.classList.add('hidden');
      if (libraryContainer) libraryContainer.classList.add('hidden');
      if (exerciseContainer) exerciseContainer.classList.remove('hidden');
      if (adminContainer) adminContainer.classList.add('hidden');
      // (Phase E pivot) Removed two-column layout switching logic
      
      // Prevent unwanted scrolling when switching to exercise mode
      window.scrollTo(0, 0);
      
      // Focus on player section when entering exercise mode
      const playerSection = document.getElementById('player-section');
      if (playerSection) {
        setTimeout(() => playerSection.focus({ preventScroll: true }), 50);
      }
    }
  }
}

// Create singleton instance
const router = new Router();

/**
 * Switch application mode (convenience function)
 */
export function switchMode(mode: ViewMode): void {
  router.switchMode(mode);
}

// Export router and helper function
export { router, createSVGIcon };
export default router;
